import type { JSX } from 'react';

import clsx from 'clsx';

import { horizontalPaddingClasses } from '@/components/classes/horizontalPaddingClasses';
import { hoverColorClasses } from '@/components/classes/hoverColorClasses';
import { variantClasses } from '@/components/classes/variantClasses';
import { verticalPaddingClasses } from '@/components/classes/verticalPaddingClasses';
import { testIds } from '@/components/constants/testIds';
import { composeJustifyClasses } from '@/components/functions/composeJustifyClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { ButtonType } from '@/components/types/ButtonType';
import type { Color } from '@/components/types/Color';
import type { FontWeight } from '@/components/types/FontWeight';
import type { IconType } from '@/components/types/IconType';
import type { Justify } from '@/components/types/Justify';
import type { PaddingSize } from '@/components/types/PaddingSize';
import type { Radius } from '@/components/types/Radius';
import type { Size } from '@/components/types/Size';
import type { TextDecoration } from '@/components/types/TextDecoration';
import type { TextSize } from '@/components/types/TextSize';
import type { Variant } from '@/components/types/Variant';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';

import HStack from '../HStack';
import Icon from '../Icon';
import Spinner from '../Spinner';
import Text from '../Text';

type ButtonProps = {
    children: React.ReactNode | string;
    onClick?: () => void;
    type?: ButtonType;
    variant?: Variant;
    color?: Color;
    hoverColor?: Color;
    size?: Adaptive<TextSize>;
    weight?: Adaptive<FontWeight>;
    horizontalPadding?: PaddingSize;
    verticalPadding?: PaddingSize;
    justify?: Adaptive<Justify>;
    radius?: Adaptive<Radius>;
    width?: Adaptive<Width>;
    prefixIcon?: IconType;
    suffixIcon?: IconType;
    prefixIconSize?: Adaptive<Size>;
    suffixIconSize?: Adaptive<Size>;
    isLoading?: boolean;
    loadingText?: string;
    textDecoration?: Adaptive<TextDecoration>;
    isDisabled?: boolean;
    isDimmed?: boolean;
    className?: string;
    iconClassName?: string;
    testId?: string;
    shouldPreventDefault?: boolean;
    shouldStopPropagation?: boolean;
};

export default function Button({
    children,
    onClick,
    type = componentsConfig.button.type,
    variant = componentsConfig.button.variant,
    color = componentsConfig.button.color,
    hoverColor = componentsConfig.button.hoverColor,
    size = componentsConfig.button.size,
    weight = componentsConfig.button.weight,
    horizontalPadding = componentsConfig.button.horizontalPadding,
    verticalPadding = componentsConfig.button.verticalPadding,
    justify = componentsConfig.button.justify,
    radius = componentsConfig.button.radius,
    width = componentsConfig.button.width,
    prefixIcon,
    suffixIcon,
    prefixIconSize = componentsConfig.icon.size,
    suffixIconSize = componentsConfig.icon.size,
    isLoading,
    loadingText,
    textDecoration, 
    isDisabled,
    isDimmed = true,
    className,
    iconClassName,
    testId = testIds.button.base,
    shouldPreventDefault = false,
    shouldStopPropagation = false,
}: ButtonProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const commonClasses = 'inline-flex align-middle items-center gap-2 focus:outline-none';

    const spinnerColor: Record<string, 'accent' | 'white'> = {
        solid: 'white',
        soft: 'accent',
        outline: 'accent',
        ghost: 'accent',
        link: 'accent',
    };

    // ----------------------------------------------------------------------------------------------------
    // MARK: Subcomponents
    // ----------------------------------------------------------------------------------------------------
    function renderChildren(): React.ReactNode | string {
        if (isLoading) {
            return (
                <HStack gap={2} width="auto">
                    <Spinner testId={testIds.button.spinner} color={spinnerColor[variant]} size={4} />
                    <Text size={size} weight={weight} color="none">
                        {loadingText ?? children}
                    </Text>
                </HStack>
            );
        }

        if (typeof children === 'string') {
            return (
                <Text testId={testIds.button.text} size={size} weight={weight} color="none" decoration={textDecoration}>
                    {children}
                </Text>
            );
        }

        return children;
    }

    // function to handle the click event
    function handleClick(e: React.MouseEvent<HTMLButtonElement>): void {
        if (shouldPreventDefault) {
            e.preventDefault();
        }

        if (shouldStopPropagation) {
            e.stopPropagation();
        }

        onClick?.();
    }
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <button
            type={type}
            data-testid={testId}
            className={clsx(
                commonClasses,
                variantClasses[variant][color],
                !isDisabled && hoverColorClasses[variant][hoverColor],
                variant !== 'link' && horizontalPaddingClasses[horizontalPadding],
                variant !== 'link' && verticalPaddingClasses[verticalPadding],
                composeJustifyClasses(justify),
                composeRadiusClasses(radius),
                composeWidthClasses(width),
                isDisabled && 'cursor-default',
                isDisabled && isDimmed && 'disabled:opacity-50',
                className
            )}
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            disabled={isLoading || isDisabled}
            onClick={handleClick}
        >
            {prefixIcon && <Icon testId={testIds.button.prefixIcon} icon={prefixIcon} size={prefixIconSize} className={iconClassName} />}

            {renderChildren()}

            {suffixIcon && <Icon testId={testIds.button.suffixIcon} icon={suffixIcon} size={suffixIconSize} className={iconClassName} />}
        </button>
    );
}
